const SIZE_RE = /(\d+)\s*[x×]\s*(\d+)/i;
const SIZE_TAG_RE = /^size:(.+)$/i;
const SIZE_BARE_RE = /^\d+\s*[x×]\s*\d+$/i;
const MACHINE_TAG_PREFIXES = ["size:", "slot:", "variant:", "fill:", "variant_cfg:"];

/** Pull a size label like `10x12` from tags, name, or code. */
export function extractRecipeSizeLabel(recipe: {
  code: string;
  name: string;
  tags?: readonly string[];
}): string | null {
  for (const tag of recipe.tags ?? []) {
    const m = tag.trim().match(SIZE_TAG_RE);
    if (m?.[1]) {
      const fromTag = normalizeSizeLabel(m[1]);
      if (fromTag) return fromTag;
    }
  }
  const fromName = `${recipe.name} ${recipe.code}`.match(SIZE_RE);
  if (fromName) return `${fromName[1]}x${fromName[2]}`;
  return null;
}

export function normalizeSizeLabel(raw: string): string | null {
  const m = raw.trim().match(SIZE_RE);
  if (!m) return null;
  return `${m[1]}x${m[2]}`;
}

/** Bare style tokens on recipe tags (utility, lofted, …) — skips size/slot/variant machine tags. */
export function extractRecipeStyleLabels(tags: readonly string[] | undefined): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of tags ?? []) {
    const t = raw.trim();
    if (!t) continue;
    const lower = t.toLowerCase();
    if (MACHINE_TAG_PREFIXES.some((p) => lower.startsWith(p))) continue;
    if (SIZE_BARE_RE.test(t)) continue;
    if (seen.has(lower)) continue;
    seen.add(lower);
    out.push(t);
  }
  return out;
}

export function extractRecipeStyleLabel(recipe: {
  tags?: readonly string[];
}): string | null {
  return extractRecipeStyleLabels(recipe.tags)[0] ?? null;
}

export function parseSizeList(raw: string): string[] {
  const parts = raw.split(/[,;\n]+/).map((p) => p.trim()).filter(Boolean);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const part of parts) {
    const size = normalizeSizeLabel(part);
    if (!size || seen.has(size)) continue;
    seen.add(size);
    out.push(size);
  }
  return out;
}
